import { useEffect } from 'react';
import { isNumber } from 'lodash-es';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  ButtonGhost,
  ComboBox,
  Divider,
  FormField,
  Grid,
  Label,
  Popover as _Popover,
  Select,
  TextInput,
} from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';

import { useFilterUtils } from './useFilterUtils';

import type { FilterDefBase, FilterState } from '../DataTable/types';

export interface ActionBarFilterPopupProps {
  anchorEl: HTMLElement | null | undefined;
  filterDef: FilterDefBase | null | undefined;
  filterIndex: number | null | undefined;
  filters: FilterState[];
  onClose: () => void;
  onSubmit: (filterIndex: number, filterKey: string, filterValue: unknown) => void;
  open: boolean;
}

export default function ActionBarFilterPopup(props: ActionBarFilterPopupProps) {
  const { t } = useTranslation();
  const { anchorEl, filterIndex, filterDef, filters, onClose, onSubmit, open } = props;

  const {
    filterKey,
    onFilterKeyChange,
    filterKeyOption,
    filterKeySelectOptions,
    filterValue,
    onFilterValueChange,
    filterValueSelectOptions,
    isFilterValueSelectOptionsLoading,
    isFilterChanged,
    isFilterValueEmpty,
    isFilterDuplicated,
  } = useFilterUtils({
    filterDef,
    filterIndex,
    filters,
  });

  const disabledSubmit =
    !isNumber(filterIndex) ||
    !filterKey ||
    !isFilterChanged ||
    isFilterValueEmpty ||
    isFilterDuplicated;

  const handleSubmit = () => {
    if (disabledSubmit) return;
    onSubmit(filterIndex, filterKey, filterValue);
  };

  const handleFilterValueInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    if (open) {
      onFilterKeyChange(filters.at(filterIndex ?? -1)?.key ?? null);
      onFilterValueChange(filters.at(filterIndex ?? -1)?.value ?? null);
    }
  }, [open]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      transitionDuration={0}
    >
      <Root>
        <Paper container direction="column" gap={'8px'}>
          <StyledFormField label={<Label label={t('ui:filter.property')} size="md" />} size="sm">
            <Select
              block
              options={filterKeySelectOptions}
              value={filterKey}
              onChange={onFilterKeyChange}
              autoWidth="as-input"
            />
          </StyledFormField>
          <StyledFormField label={<Label label={t('ui:filter.value')} size="md" />} size="sm">
            {filterKeyOption ? (
              filterKeyOption.type === 'select' ? (
                <Select
                  block
                  options={filterValueSelectOptions}
                  value={filterValue}
                  onChange={value => onFilterValueChange(value)}
                  placeholder={filterKeyOption.placeholder ?? t('ui:common.select.required.ph')}
                  autoWidth="as-input"
                />
              ) : filterKeyOption.autoCompleteFn ? (
                <ComboBox
                  fullWidth
                  options={filterValueSelectOptions.map(o => o.text ?? '')}
                  value={typeof filterValue === 'string' ? filterValue : ''}
                  onChange={(event, value) => onFilterValueChange(value)}
                  placeholder={filterKeyOption.placeholder ?? t('common:common.prompt.enter_value')}
                  loading={isFilterValueSelectOptionsLoading}
                  getOptionDisabled={option =>
                    filterValueSelectOptions?.some(o => o.text === option && o.disabled) ?? false
                  }
                />
              ) : (
                <TextInput
                  fullWidth
                  value={filterValue || ''}
                  onChange={event => onFilterValueChange(event.target.value)}
                  onClear={() => onFilterValueChange('')}
                  onKeyDown={handleFilterValueInputKeyDown}
                  placeholder={filterKeyOption.placeholder ?? t('common:common.prompt.enter_value')}
                />
              )
            ) : (
              <></>
            )}
          </StyledFormField>

          <Divider />
          <Grid container justifyContent="flex-end" gap={'8px'} css={{ margin: '4px 0px' }}>
            <ButtonGhost size="md" variant="secondary" onClick={onClose}>
              {t('common:common.action.cancel')}
            </ButtonGhost>
            <Button size="md" variant="primary" disabled={disabledSubmit} onClick={handleSubmit}>
              {t('common:common.action.apply')}
            </Button>
          </Grid>
        </Paper>
      </Root>
    </Popover>
  );
}

const Popover = styled(_Popover)``;

const Root = styled('div')``;

const Paper = styled(Grid)(
  ({ theme }) => css`
    border-radius: 2px;
    background-color: ${theme.color['surface/default']};
    box-shadow: ${theme.elevation['level_4']};
    padding: 8px 16px;
    width: 232px;
  `,
);

const StyledFormField = styled(FormField)`
  width: 100%;
`;
